
import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { useProfile } from '@/hooks/profile/useProfile';

interface EmailRecipientFieldsProps {
  toAddress: string;
  ccAddress: string;
  bccAddress: string;
  subject: string;
  showCcBcc: boolean;
  setToAddress: (value: string) => void;
  setCcAddress: (value: string) => void;
  setBccAddress: (value: string) => void;
  setSubject: (value: string) => void;
  setShowCcBcc: (show: boolean) => void;
  handleChange: () => void;
}

const EmailRecipientFields: React.FC<EmailRecipientFieldsProps> = ({
  toAddress,
  ccAddress,
  bccAddress,
  subject,
  showCcBcc,
  setToAddress,
  setCcAddress,
  setBccAddress,
  setSubject,
  setShowCcBcc,
  handleChange,
}) => {
  const { profile, loading } = useProfile();
  const [showCc, setShowCc] = React.useState(false);
  const [showBcc, setShowBcc] = React.useState(false);

  return (
    <div className="space-y-1">
      {/* From Field */}
      <div className="flex items-center py-1">
        <div className="w-12 text-sm text-muted-foreground shrink-0">From</div>
        <div className="flex-1 flex items-center justify-between px-2 py-1 text-sm">
          <span>
            {loading ? 'Loading...' : profile ? `${profile.email_full_name || `${profile.first_name} ${profile.last_name}`} <${profile.imap_email || profile.email}>` : 'Profile not loaded'}
          </span>
          <ChevronDown className="h-3 w-3 text-muted-foreground ml-2" />
        </div>
      </div>

      {/* To Field with separate Cc/Bcc buttons */}
      <div className="flex items-center py-1">
        <div className="w-12 text-sm text-muted-foreground shrink-0">To</div>
        <Input 
          value={toAddress} 
          onChange={(e) => {
            setToAddress(e.target.value);
            handleChange();
          }}
          placeholder="Recipients" 
          className="flex-1 border-none shadow-none px-2 py-1 h-8 focus-visible:ring-0"
        />
        <div className="flex items-center space-x-1 ml-2">
          {!showCc && (
            <Button 
              variant="ghost" 
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setShowCc(true)}
            >
              Cc
            </Button>
          )}
          {!showBcc && (
            <Button 
              variant="ghost" 
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={() => setShowBcc(true)}
            >
              Bcc
            </Button>
          )}
        </div>
      </div>

      {/* Cc Field */}
      {showCc && (
        <div className="flex items-center py-1">
          <div className="w-12 text-sm text-muted-foreground shrink-0">Cc</div>
          <Input 
            value={ccAddress} 
            onChange={(e) => {
              setCcAddress(e.target.value);
              handleChange();
            }}
            placeholder="Carbon copy" 
            className="flex-1 border-none shadow-none px-2 py-1 h-8 focus-visible:ring-0"
          />
          <Button 
            variant="ghost" 
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground ml-2"
            onClick={() => setShowCc(false)}
          >
            ×
          </Button>
        </div>
      )}

      {/* Bcc Field */}
      {showBcc && (
        <div className="flex items-center py-1">
          <div className="w-12 text-sm text-muted-foreground shrink-0">Bcc</div>
          <Input 
            value={bccAddress} 
            onChange={(e) => {
              setBccAddress(e.target.value);
              handleChange();
            }}
            placeholder="Blind carbon copy" 
            className="flex-1 border-none shadow-none px-2 py-1 h-8 focus-visible:ring-0"
          />
          <Button 
            variant="ghost" 
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground ml-2"
            onClick={() => setShowBcc(false)}
          >
            ×
          </Button>
        </div>
      )}

      {/* Subject Field */}
      <div className="flex items-center py-1 border-b">
        <div className="w-12 text-sm text-muted-foreground shrink-0">Subject</div>
        <Input 
          value={subject} 
          onChange={(e) => {
            setSubject(e.target.value);
            handleChange();
          }}
          placeholder="Subject" 
          className="flex-1 border-none shadow-none px-2 py-1 h-8 focus-visible:ring-0"
        />
      </div>
    </div>
  );
};

export default EmailRecipientFields;
